import { useMemo } from 'react';

import { useGetTicket, useShowCampaignSettings, useShowEvent } from '@givebacks/api/store';
import { useForm } from '@givebacks/formy';
import { DateTime } from 'luxon';
import {
  Alert,
  Box,
  Checkbox,
  CurrencyInput,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from 'ui';
import { isNotEmpty } from 'ui/Form';
import { IconInfo } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../../../../../Context';
import { AddEditTicketFormType } from './AddTicketForm.type';
import { AdditionalSettings } from './AdditionalSettings';
import { IncludedEventDates } from './components/IncludedEventDates';
import SeasonTicketSalesEndInputs from './components/SeasonTicketSalesEndInputs';
import SeasonTicketSalesStartInputs from './components/SeasonTicketSalesStartInputs';
import TicketSalesEndInputs from './components/TicketSalesEndInputs';
import TicketSalesStartInputs from './components/TicketSalesStartInputs';

const AddEditTicketForm = ({
  ticketId,
  isDuplicate,
  isSeasonTicket,
}: {
  ticketId?: string;
  isDuplicate?: boolean;
  isSeasonTicket?: boolean;
}) => {
  const { causeId, campaignId } = useCrowdfundingProvider();

  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId,
    causeId,
  });

  const { data: ticketData } = useGetTicket({
    ticketId,
    causeId,
    eventId: campaignId,
    ticketsType: isSeasonTicket ? 'season_passes' : 'tickets',
  });

  const { data: event } = useShowEvent({
    eventId: campaignId,
    causeId,
  });

  const isLimitedEditing = useMemo(() => {
    if (ticketId) {
      if (isDuplicate) {
        return false;
      }
      return campaignSettings.isCampaignPublished || campaignSettings.isCampaignScheduled;
    }
    return false;
  }, [
    campaignSettings.isCampaignPublished,
    campaignSettings.isCampaignScheduled,
    isDuplicate,
    ticketId,
  ]);

  const defaultEventTicketEndType = useMemo(() => {
    if (ticketData?.ticket_window_data?.end?.units === 'hours') {
      return 'one_hour_after_event_starts';
    }

    if (ticketData?.ticket_window_data?.end?.type) {
      return ticketData?.ticket_window_data?.end?.type;
    }

    return 'one_hour_after_event_starts';
  }, [ticketData?.ticket_window_data?.end?.units, ticketData?.ticket_window_data?.end?.type]);

  const ticketWindowStart = useMemo(() => {
    if (isSeasonTicket) {
      return {
        type: ticketData?.ticket_window_data?.start?.type ?? 'on_publish_date',
        date:
          ticketData?.ticket_window_data?.start?.type === 'custom_date' && ticketData?.starting_at
            ? DateTime.fromISO(ticketData?.starting_at).toJSDate()
            : undefined,
      };
    }

    return {
      type: ticketData?.ticket_window_data?.start?.type ?? 'on_publish_date',
      delta: ticketData?.ticket_window_data?.start?.delta ?? undefined,
      timestamp: ticketData?.ticket_window_data?.start?.timestamp ?? undefined,
    };
  }, [
    isSeasonTicket,
    ticketData?.starting_at,
    ticketData?.ticket_window_data?.start?.delta,
    ticketData?.ticket_window_data?.start?.timestamp,
    ticketData?.ticket_window_data?.start?.type,
  ]);

  const ticketWindowEnd = useMemo(() => {
    if (isSeasonTicket) {
      return {
        type: ticketData?.ticket_window_data?.end?.type ?? 'after_event',
        date:
          ticketData?.ticket_window_data?.end?.type === 'custom_date' && ticketData?.expires_at
            ? DateTime.fromISO(ticketData?.expires_at).toJSDate()
            : undefined,
      };
    }

    return {
      type: defaultEventTicketEndType,
      delta: ticketData?.ticket_window_data?.end?.delta ?? 1,
      timestamp: ticketData?.ticket_window_data?.end?.timestamp ?? undefined,
    };
  }, [
    defaultEventTicketEndType,
    isSeasonTicket,
    ticketData?.expires_at,
    ticketData?.ticket_window_data?.end?.delta,
    ticketData?.ticket_window_data?.end?.timestamp,
    ticketData?.ticket_window_data?.end?.type,
  ]);

  const eventDatesIds = useMemo(() => {
    if (ticketData?.type === 'StoreItems::SeasonPass') {
      return ticketData?.event_dates_ids;
    } else {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData?.type]);
  const form = useForm<AddEditTicketFormType>(
    {
      initialValues: {
        name: isDuplicate ? `${ticketData?.name} (Copy)` : (ticketData?.name ?? ''),
        price: ticketData?.price ?? 0,
        ticket_type: ticketData?.ticket_type ?? 'paid',
        unlimited: ticketData?.unlimited ?? false,
        available_quantity: ticketData?.available_quantity ?? undefined,
        included_event_dates: eventDatesIds ?? ['*'],
        ticket_window_data: {
          start: ticketWindowStart,
          end: ticketWindowEnd,
        },
        description: ticketData?.description ?? '',
      },
      validate: {
        name: isNotEmpty('Name is required'),
        price: (value, values) => {
          if (Number(value) < 100 && values.ticket_type !== 'free') {
            return 'Price must be at least $1';
          }
          return null;
        },
        available_quantity: (value, values) => {
          if (values.unlimited) return null;
          if (!value) return 'Field cannot be empty';
          if (value < 1) return 'Quantity must be at least 1';
          return null;
        },
        ticket_window_data: {
          start: {
            delta: (value, values) => {
              if (
                values.ticket_window_data?.start?.type === 'x_time_before_event_date' &&
                values.ticket_window_data.start.units === 'days' &&
                !value
              ) {
                return 'Field cannot be empty';
              }
              return null;
            },
            timestamp: (value, values) => {
              if (values.ticket_window_data?.start?.type === 'on_event_date' && !value) {
                return true;
              }
              return null;
            },
            date: (value, values) => {
              if (values.ticket_window_data?.start?.type === 'custom_date') {
                if (!value) {
                  return 'Field cannot be empty';
                }
                if (
                  event?.published_at &&
                  DateTime.fromJSDate(value) < DateTime.fromISO(event?.published_at)
                ) {
                  return true;
                }
              }

              return null;
            },
          },
          end: {
            delta: (value, values) => {
              if (values.ticket_window_data?.end?.units === 'days' && !value) {
                return true;
              }
              return null;
            },
            timestamp: (value, values) => {
              if (values.ticket_window_data?.end?.type === 'on_event_date' && !value) {
                return true;
              }
              return null;
            },
            date: (value, values) => {
              if (values.ticket_window_data?.end?.type === 'custom_date') {
                if (!value) {
                  return true;
                }
                if (
                  event?.expires_at &&
                  DateTime.fromJSDate(value) > DateTime.fromISO(event?.expires_at)
                ) {
                  return true;
                }
              }
              return null;
            },
          },
        },
        included_event_dates: isSeasonTicket ? isNotEmpty('Field cannot be empty') : undefined,
      },
      enhanceGetInputProps: (payload) => {
        if (campaignSettings.isCampaignReadOnly) {
          return {
            readOnly: true,
            disabled: payload.field === 'unlimited',
          };
        }

        if (isLimitedEditing) {
          return {
            readOnly: payload.field === 'ticket_type' || payload.field === 'available_quantity',
            disabled: payload.field === 'unlimited',
          };
        }

        return {
          disabled:
            (!!payload.form.values.unlimited && payload.field === 'available_quantity') ||
            (payload.form.values.ticket_type === 'free' && payload.field === 'price'),
        };
      },
    },
    {
      id: 'addEditTicketForm',
    }
  );

  const customDateError = useMemo(() => {
    if (Object.keys(form.errors).includes('ticket_window_data.start.date')) {
      if (event?.starting_at) {
        return 'start';
      }
      return null;
    }
    if (Object.keys(form.errors).includes('ticket_window_data.end.date')) {
      if (event?.expires_at) {
        return 'end';
      }
      return null;
    }
    return null;
  }, [event?.expires_at, event?.starting_at, form.errors]);

  const publishRange = useMemo(() => {
    if (event?.starting_at && event?.expires_at) {
      return `${DateTime.fromISO(event?.starting_at).toLocaleString(
        DateTime.DATE_SHORT
      )} - ${DateTime.fromISO(event?.expires_at).toLocaleString(DateTime.DATE_SHORT)}`;
    }
    return null;
  }, [event?.expires_at, event?.starting_at]);

  return (
    <Stack>
      <SimpleGrid cols={2}>
        <TextInput
          required
          label={'Name'}
          placeholder={`Enter ticket name`}
          {...form.getInputProps('name')}
        />
        <Box />
        <Select
          label="Ticket Type"
          placeholder={`Paid`}
          data={[
            { label: 'Paid', value: 'paid' },
            { label: 'Free', value: 'free' },
          ]}
          required
          {...form.getInputProps('ticket_type')}
        />
        <CurrencyInput label={'Price'} {...form.getInputProps('price')} />
        <NumberInput
          label={
            <Group justify="space-between">
              <Text type="label">Available Quantity</Text>
              <Tooltip
                label="This quantity will be available for each date the ticket is applied."
                position="bottom"
                w="300px"
              >
                <IconInfo width={16} />
              </Tooltip>
            </Group>
          }
          placeholder={`Enter quantity`}
          {...form.getInputProps('available_quantity')}
        />
        <Checkbox
          label="Unlimited"
          style={{ alignContent: 'end' }}
          mb={8}
          {...form.getInputProps('unlimited')}
          checked={form.values.unlimited}
        />
      </SimpleGrid>
      <Divider />
      {isSeasonTicket ? (
        <>
          <IncludedEventDates form={form} />
          <Divider />
          {customDateError && (
            <Alert.Collapse
              in={true}
              type="error"
              title={`Ticket sales ${customDateError} date falls outside the publish range`}
            >
              The ticket sales {customDateError} must be within the publish dates ({publishRange}).
            </Alert.Collapse>
          )}
          <SeasonTicketSalesStartInputs form={form} />
          <SeasonTicketSalesEndInputs form={form} />
        </>
      ) : (
        <>
          <TicketSalesStartInputs form={form} />
          <TicketSalesEndInputs form={form} />
        </>
      )}
      <Divider />
      <AdditionalSettings form={form} options={ticketData?.options} isOpen={isDuplicate ?? false} />
    </Stack>
  );
};

const AddEditTicketContent = ({
  ticketId,
  isDuplicate,
  storeItemId,
  causeId,
  isSeasonTicket,
}: {
  ticketId?: string;
  storeItemId?: string;
  causeId?: string;
  isDuplicate?: boolean;
  isSeasonTicket?: boolean;
}) => {
  const { isLoading } = useGetTicket({
    ticketId,
    causeId,
    eventId: storeItemId,
    ticketsType: isSeasonTicket ? 'season_passes' : 'tickets',
  });

  return (
    <Box mih={120}>
      <LoadingOverlay visible={isLoading} />
      {!isLoading && (
        <AddEditTicketForm
          ticketId={ticketId}
          isDuplicate={isDuplicate}
          isSeasonTicket={isSeasonTicket}
        />
      )}
    </Box>
  );
};

export { AddEditTicketContent };
